import { useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, BookOpen, Layers,
  Play, ChevronLeft, ChevronRight,
} from "lucide-react";
import { PROFILE, BLOG_POSTS, PROJECTS, VIDEOS, FEEDBACKS } from "../data/content";

// ── Carousel ──────────────────────────────────────────────────────────────────
function Carousel({ items, renderItem }) {
  const ref = useRef(null);
  const touchStartX = useRef(null);
  const touchStartScroll = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const scroll = dir => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.offsetWidth / 3 + 16), behavior: "smooth" });
  };

  const onTouchStart = useCallback(e => {
    touchStartX.current = e.touches[0].clientX;
    touchStartScroll.current = ref.current?.scrollLeft ?? 0;
    setIsDragging(false);
  }, []);

  const onTouchMove = useCallback(e => {
    if (touchStartX.current === null || !ref.current) return;
    const dx = touchStartX.current - e.touches[0].clientX;
    if (Math.abs(dx) > 5) setIsDragging(true);
    ref.current.scrollLeft = touchStartScroll.current + dx;
  }, []);

  const onTouchEnd = useCallback(() => {
    touchStartX.current = null;
    touchStartScroll.current = null;
    setTimeout(() => setIsDragging(false), 0);
  }, []);

  const btnBase = {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: 36, height: 36, borderRadius: "50%",
    background: "#fff", border: "1px solid #E4E4E7",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", zIndex: 2,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    transition: "border-color 200ms, box-shadow 200ms",
  };
  const hover = e => { e.currentTarget.style.borderColor = "#09090B"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; };
  const leave = e => { e.currentTarget.style.borderColor = "#E4E4E7"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; };
  return (
    <div style={{ position: "relative" }}>
      <button style={{ ...btnBase, left: -18 }} onClick={() => scroll(-1)} onMouseOver={hover} onMouseOut={leave}>
        <ChevronLeft size={15} color="#3F3F46" />
      </button>
      <div
        ref={ref}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          display: "flex", gap: 16,
          overflowX: "hidden",
          scrollSnapType: isDragging ? "none" : "x mandatory",
          cursor: isDragging ? "grabbing" : "default",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {items.map((item, i) => (
          <div key={i} className="carousel-item">
            {renderItem(item)}
          </div>
        ))}
      </div>
      <button style={{ ...btnBase, right: -18 }} onClick={() => scroll(1)} onMouseOver={hover} onMouseOut={leave}>
        <ChevronRight size={15} color="#3F3F46" />
      </button>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ label, title, subtitle, linkTo, linkLabel, children }) {
  return (
    <section style={{ marginBottom: 80 }}>
      <div className="reveal" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 28 }}>
        <div>
          <span className="section-label">{label}</span>
          <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 22, color: "#09090B", margin: 0, lineHeight: 1.25 }}>{title}</h2>
          {subtitle && <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#64748B", margin: "5px 0 0" }}>{subtitle}</p>}
        </div>
        {linkTo && (
          <Link to={linkTo} style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 13, color: "#2563EB", textDecoration: "none", display: "flex", alignItems: "center", gap: 4, transition: "opacity 200ms", whiteSpace: "nowrap" }}
            onMouseOver={e => e.currentTarget.style.opacity = "0.65"}
            onMouseOut={e => e.currentTarget.style.opacity = "1"}
          >{linkLabel} <ArrowRight size={13} /></Link>
        )}
      </div>
      <div className="reveal reveal-delay-1">{children}</div>
    </section>
  );
}

// ── Cards ─────────────────────────────────────────────────────────────────────
function ProjectCard({ project }) {
  return (
    <div className="card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {project.thumbnail
        ? <img src={project.thumbnail} alt={project.title} style={{ width: "100%", height: 128, objectFit: "cover", flexShrink: 0 }} />
        : <div style={{ width: "100%", height: 128, background: "#E8ECF0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 38, flexShrink: 0 }}>{project.emoji}</div>
      }
      <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 14, color: "#09090B", lineHeight: 1.4, marginBottom: 6 }}>{project.title}</div>
        <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#64748B", lineHeight: 1.6, flex: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{project.description}</div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 10 }}>
          {project.tags.slice(0, 3).map(t => (
            <span key={t} className="tag tag-blue" style={{ fontSize: 10 }}>{t}</span>
          ))}
        </div>
      </div>
      <div style={{ padding: "0 16px 14px", display: "flex", justifyContent: "flex-end" }}>
        <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 12, color: "#2563EB", display: "flex", alignItems: "center", gap: 3 }}>
          Xem <ArrowUpRight size={12} />
        </span>
      </div>
    </div>
  );
}

function BlogCard({ post }) {
  return (
    <div className="card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {post.thumbnail
        ? <img src={post.thumbnail} alt={post.title} style={{ width: "100%", height: 110, objectFit: "cover", flexShrink: 0 }} />
        : <div style={{ width: "100%", height: 110, background: "#E8ECF0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, flexShrink: 0 }}>{post.thumbnailEmoji}</div>
      }
      <div style={{ padding: "12px 14px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 7 }}>
          <span className="tag tag-blue" style={{ fontSize: 10 }}>{post.category}</span>
          <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, color: "#64748B" }}>{post.date}</span>
        </div>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 600, fontSize: 13, color: "#09090B", lineHeight: 1.45, flex: 1, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{post.title}</div>
      </div>
      <div style={{ padding: "0 14px 12px" }}>
        <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 12, color: "#2563EB", display: "flex", alignItems: "center", gap: 3 }}>Đọc <ArrowUpRight size={12} /></span>
      </div>
    </div>
  );
}

function VideoCard({ video }) {
  const thumbId = video.embedUrl?.split("/embed/")[1];
  return (
    <div className="card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ position: "relative", height: 180, background: "#09090B", flexShrink: 0, overflow: "hidden" }}>
        {thumbId
          ? <img src={`https://img.youtube.com/vi/${thumbId}/hqdefault.jpg`} alt={video.title} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.82 }} />
          : <div style={{ width: "100%", height: "100%", background: "#E8ECF0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34 }}>{video.emoji}</div>
        }
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
            <Play size={12} color="#09090B" fill="#09090B" style={{ marginLeft: 2 }} />
          </div>
        </div>
        {video.duration && <span style={{ position: "absolute", bottom: 6, right: 8, background: "rgba(0,0,0,0.72)", color: "#fff", fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4, fontFamily: "'Space Grotesk',sans-serif" }}>{video.duration}</span>}
      </div>
      <div style={{ padding: "12px 14px", flex: 1 }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 600, fontSize: 13, color: "#09090B", lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{video.title}</div>
      </div>
    </div>
  );
}

function FeedbackCard({ f }) {
  const thumbId = f.embedUrl?.split("/embed/")[1];
  return (
    <div className="card" style={{ padding: 0, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* YouTube thumbnail */}
      {thumbId && (
        <div style={{ position: "relative", paddingBottom: "56.25%", background: "#0F172A", flexShrink: 0 }}>
          <img
            src={`https://img.youtube.com/vi/${thumbId}/hqdefault.jpg`}
            alt={f.name}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.2)" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.92)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Play size={12} color="#09090B" fill="#09090B" style={{ marginLeft: 2 }} />
            </div>
          </div>
        </div>
      )}
      <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
        <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#3F3F46", lineHeight: 1.65, flex: 1, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", margin: 0 }}>{f.quote}</p>
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F4F4F5", display: "flex", alignItems: "center", gap: 8 }}>
          <div>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 12, color: "#09090B" }}>{f.name}</div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, color: "#64748B" }}>{f.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Home() {
  return (
    <div style={{ background: "#FAFAFA", minHeight: "100vh", fontFamily: "'Space Grotesk',sans-serif" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px 80px" }}>

        {/* ── HERO ── */}
        <section className="hero-section" style={{ padding: "80px 0 88px" }}>
          <div className="hero-inner">

            {/* Left: badge + headline + bio + CTA + stats */}
            <div style={{ flex: 1 }}>
              {/* Availability badge */}
              <div className="reveal" style={{ marginBottom: 32 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 600, color: "#3F3F46", background: "#fff", border: "1px solid #E4E4E7", padding: "5px 12px", borderRadius: 99, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 0 2px rgba(34,197,94,0.25)" }} />
                  Website Portfolio 2026
                </span>
              </div>

              <div className="reveal reveal-delay-1">
                <h1 style={{
                  fontFamily: "'Archivo',sans-serif",
                  fontWeight: 900,
                  fontSize: "clamp(36px, 5vw, 72px)",
                  lineHeight: 1.04,
                  letterSpacing: "-2px",
                  color: "#09090B",
                  margin: "0 0 24px",
                }}>
                  Dược sĩ.{" "}
                  <span style={{ color: "#2563EB" }}>Content Creator.</span>
                  {" "}Tech-savvy creative.
                </h1>
                <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 17, color: "#64748B", lineHeight: 1.7, margin: "0 0 28px" }}>
                  {PROFILE.bio}
                </p>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 40 }}>
                  <Link to="/projects" style={{ textDecoration: "none" }}>
                    <button className="btn-primary" style={{ height: 44, padding: "0 22px", fontSize: 14 }}>
                      <Layers size={15} /> Xem Dự án
                    </button>
                  </Link>
                  <Link to="/blog" style={{ textDecoration: "none" }}>
                    <button className="btn-secondary" style={{ height: 44, padding: "0 22px", fontSize: 14 }}>
                      <BookOpen size={15} /> Đọc Blog
                    </button>
                  </Link>
                </div>
              </div>

              {/* Stats strip */}
              <div className="reveal reveal-delay-3 hero-stats" style={{ display: "flex", gap: 0, borderTop: "1px solid #E4E4E7", paddingTop: 28 }}>
                {[
                  { value: `${BLOG_POSTS.length}+`, label: "Bài viết" },
                  { value: `${PROJECTS.length}+`,   label: "Dự án" },
                  { value: `${VIDEOS.length}+`,     label: "Videos" },
                  { value: "2023",                  label: "Tốt nghiệp" },
                ].map((s, i) => (
                  <div key={s.label} style={{ flex: 1, paddingRight: 24, borderRight: i < 3 ? "1px solid #E4E4E7" : "none", marginRight: i < 3 ? 24 : 0 }}>
                    <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 26, color: "#09090B", lineHeight: 1.1 }}>{s.value}</div>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#64748B", marginTop: 3 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: avatar full height */}
            {PROFILE.avatar && (
              <div className="reveal reveal-delay-1 hero-avatar">
                <img
                  src={PROFILE.avatar}
                  alt={PROFILE.name}
                  style={{ height: "100%", maxHeight: 480, width: "auto", objectFit: "contain", objectPosition: "center bottom", display: "block" }}
                />
              </div>
            )}

          </div>
        </section>

        {/* ── ABOUT (Skill: Neutral bg, let work shine) ── */}
        <section style={{ marginBottom: 80 }}>
          <div className="reveal" style={{ marginBottom: 28 }}>
            <span className="section-label">Về mình</span>
          </div>
          <div className="about-grid">

            {/* Dark bio card */}
            <div className="reveal about-dark-card" style={{ background: "#09090B", borderRadius: 12, padding: "32px 28px", display: "flex", flexDirection: "column" }}>
              <div style={{ flex: 1 }}>
                <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", margin: "0 0 16px", lineHeight: 1.3, letterSpacing: "-0.5px" }}>
                  Không chỉ<br />là Dược sĩ.
                </h2>
                <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.75, margin: "0 0 20px" }}>{PROFILE.intro}</p>
                <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#fff", lineHeight: 1.6 }}>
                  {PROFILE.major} · {PROFILE.year}<br />Digital Marketing · Green Academy<br />Marketing Dược · BMG
                </p>
              </div>
              <Link to="/resume" style={{ textDecoration: "none", marginTop: 28 }}>
                <button style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 13, background: "#fff", color: "#09090B", border: "none", borderRadius: 8, height: 38, padding: "0 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "background 200ms" }}
                  onMouseOver={e => e.currentTarget.style.background="#E8ECF0"}
                  onMouseOut={e => e.currentTarget.style.background="#fff"}
                >Xem CV <ArrowUpRight size={13} /></button>
              </Link>
            </div>

            {/* 4 skill cards */}
            {[
              { label: "CHUYÊN MÔN", title: "Dược sĩ làm Content", desc: "Tốt nghiệp Dược nhưng đam mê sáng tạo nội dung, thiết kế và video.", accent: "#2563EB" },
              { label: "PHƯƠNG PHÁP", title: "Học bằng cách làm", desc: "Mở tool lên, thử, hỏng, thử lại, rồi chia sẻ lại những gì hoạt động.", accent: "#09090B" },
              { label: "CÔNG CỤ", title: "AI thực chiến", desc: "Tích hợp AI vào quy trình thực tế, không chỉ dùng mà còn hiểu tại sao.", accent: "#2563EB" },
              { label: "MỤC TIÊU", title: "Từ Dược sang Content", desc: "Không phải bỏ nghề, mà mở rộng. Dược học cách đọc bằng chứng, Content dạy mình cách kể chuyện.", accent: "#09090B" },
            ].map((item, i) => (
              <div key={item.title} className={`reveal reveal-delay-${i + 1}`} style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 12, padding: "20px 20px 22px" }}>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: item.accent, marginBottom: 8 }}>{item.label}</div>
                <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 15, color: "#09090B", marginBottom: 8, lineHeight: 1.3 }}>{item.title}</div>
                <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#64748B", lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── PROJECTS (Skill: Visuals first) ── */}
        <Section label="Dự án nổi bật" title="Những gì mình đã làm" subtitle="Tổng hợp project thực tế" linkTo="/projects" linkLabel="Tất cả dự án">
          <Carousel items={PROJECTS} renderItem={p => (
            <Link to={`/projects/${p.id}`} style={{ textDecoration: "none", display: "block", height: "100%" }}>
              <ProjectCard project={p} />
            </Link>
          )} />
        </Section>

        {/* ── VIDEOS ── */}
        <Section label="Video" title="Video mới nhất" subtitle="Dự án video AI" linkTo="/videos" linkLabel="Tất cả video">
          <Carousel items={VIDEOS} renderItem={v => (
            <Link to={`/videos/${v.id}`} style={{ textDecoration: "none", display: "block", height: "100%" }}>
              <VideoCard video={v} />
            </Link>
          )} />
        </Section>

        {/* ── BLOG ── */}
        <Section label="Blog" title="Bài viết mới nhất" subtitle="Góc nhìn của tôi" linkTo="/blog" linkLabel="Tất cả bài viết">
          <Carousel items={BLOG_POSTS} renderItem={post => (
            <Link to={`/blog/${post.id}`} style={{ textDecoration: "none", display: "block", height: "100%" }}>
              <BlogCard post={post} />
            </Link>
          )} />
        </Section>

        {/* ── FEEDBACK ── */}
        <Section label="Nhận xét" title="Người khác nói gì" linkTo="/feedback" linkLabel="Xem tất cả">
          <Carousel items={FEEDBACKS.filter(f => f.type === "colleague")} renderItem={f => (
            <Link to={`/feedback#feedback-${f.id}`} style={{ textDecoration: "none", display: "block", height: "100%" }}>
              <FeedbackCard f={f} />
            </Link>
          )} />
        </Section>

        {/* ── CONTACT CTA (Skill: Footer Contact) ── */}
        <section className="reveal">
          <div className="cta-section" style={{ background: "#09090B", borderRadius: 16 }}>
            <div style={{ maxWidth: 500 }}>
              <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 900, fontSize: 30, color: "#fff", margin: "10px 0 12px", lineHeight: 1.2, letterSpacing: "-0.5px" }}>
                Sẵn sàng làm việc cùng nhau?
              </h2>
              <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "rgba(255,255,255,0.5)", margin: 0, lineHeight: 1.65 }}>
                Hy vọng có cơ hội trao đổi và hợp tác cùng quý Công ty/Đối tác trong tương lai.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Link to="/contact" style={{ textDecoration: "none" }}>
                <button style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 14, background: "#fff", color: "#09090B", border: "none", borderRadius: 8, height: 44, padding: "0 24px", cursor: "pointer", display: "flex", alignItems: "center", gap: 7, transition: "background 200ms" }}
                  onMouseOver={e => e.currentTarget.style.background="#E8ECF0"}
                  onMouseOut={e => e.currentTarget.style.background="#fff"}
                >Liên hệ <ArrowRight size={15} /></button>
              </Link>
              <Link to="/resume" style={{ textDecoration: "none" }}>
                <button style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 600, fontSize: 14, background: "transparent", color: "rgba(255,255,255,0.65)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 8, height: 44, padding: "0 22px", cursor: "pointer", transition: "border-color 200ms, color 200ms" }}
                  onMouseOver={e => { e.currentTarget.style.borderColor="rgba(255,255,255,0.45)"; e.currentTarget.style.color="#fff"; }}
                  onMouseOut={e => { e.currentTarget.style.borderColor="rgba(255,255,255,0.18)"; e.currentTarget.style.color="rgba(255,255,255,0.65)"; }}
                >Xem CV</button>
              </Link>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
